// @flow
import {CommonActions} from '@react-navigation/native';
import {all, call, put, select, takeEvery} from 'redux-saga/effects';
import {tripica} from '../../api';
import Navigation from '../../lib/navigation';
import {getESimProductPriceSaga} from '../../modules/ESim/sagas';
import {fetchAvailableOptions} from '../AvailableOption/sagas';
import {firstAvailableCreditableOptionsForActivationOrderSelector} from '../AvailableOption/selectors';
import {
  currentSubscriptionPortabilityCharacteristicsSelector,
  currentSubscriptionSelector,
} from '../Dashboard';
import {initNewOffer, setNewOffer} from '../NewOffer/actions';
import {fetchAllPlans} from '../Plan/sagas';
import {planSelector} from '../Plan/selectors';
import {setGodfatherExternalId, setPromotion} from '../SignUp/Bonus';
import {setKeptPhoneNumber, setPickedPhoneNumber} from '../SignUp/PhoneNumber';
import {getSimProductPriceSaga} from '../SignUp/Sim/sagas';
import {initStateWithOffer, resetOfferState} from './actions';
import {computeOfferPrice} from './lib/computeOfferPrice';
import WizzeeLog from '../../lib/wizzeeLog';

export function* initStateWithOfferSaga(
  action: {
    payload: {
      initialOffer?: {
        plan?: Object,
        options?: Object,
        price?: number,
      },
    },
  } = {payload: {}},
): Generator<*, *, *> {
  const response = yield call(fetchAllPlans);
  if (response && response.error) {
    return response;
  }

  yield call(getESimProductPriceSaga);
  yield call(getSimProductPriceSaga);
  yield call(fetchAvailableOptions);
  const {initialOffer} = action.payload;
  yield call(initNewOfferSaga, initNewOffer(initialOffer, true));
}

export function* initNewOfferSaga(action: any): Generator<*, *, *> {
  const {initialOffer, noNavigation} = action.payload;

  const offer = {};

  if (initialOffer && initialOffer.plan != null) {
    offer.plan = initialOffer.plan;
  } else {
    offer.plan = yield select(planSelector);
  }

  if (initialOffer && initialOffer.options != null) {
    offer.options = initialOffer.options;
  } else {
    //Assuming orderType is 'ACTIVATION'
    offer.options = yield select(
      firstAvailableCreditableOptionsForActivationOrderSelector,
    );
  }

  offer.price = computeOfferPrice(offer);

  yield put(setNewOffer(offer));

  yield put(setPromotion(null));

  if (!noNavigation) {
    if (initialOffer) {
      yield call(
        Navigation.dispatch,
        CommonActions.navigate({name: 'changePlan'}),
      );
    } else {
      yield call(
        Navigation.dispatch,
        CommonActions.navigate({name: 'selectPlan'}),
      );
    }
  }
}

export function* initNewOfferWithSelectedSubscription(): Generator<*, *, *> {
  const subscription = yield select(currentSubscriptionSelector);
  if (!subscription) {
    yield call(initStateWithOfferSaga, resetOfferState());
    return;
  }

  yield call(
    initStateWithOfferSaga,
    initStateWithOffer({
      options: subscription.options,
    }),
  );

  const portabilityCharacteristics = yield select(
    currentSubscriptionPortabilityCharacteristicsSelector,
  );
  if (portabilityCharacteristics) {
    yield put(
      setKeptPhoneNumber(
        portabilityCharacteristics.MSISDN,
        portabilityCharacteristics.portabilityCode,
      ),
    );
  } else if (subscription.line) {
    WizzeeLog.log('Setting picked phone number from subscription line', subscription.line);
    yield put(
      setPickedPhoneNumber(
        subscription.line.resourceOuid,
        subscription.line.number,
      ),
    );
  }

  const godfatherExternalId = subscription.godfatherExternalId;
  if (godfatherExternalId) {
    yield put(setGodfatherExternalId(godfatherExternalId));
  }

  if (subscription.promo && subscription.promo.code) {
    try {
      const response = yield call(
        [tripica, 'getPromoCode'],
        subscription.promo.code,
      );
      WizzeeLog.log('Setting promotion from subscription promo code', response);
      yield put(setPromotion(response));
    } catch (e) {
      WizzeeLog.warn(initNewOfferWithSelectedSubscription, e);
    }
  }
}

export default function* (): Generator<*, *, *> {
  yield all([
    takeEvery('INIT_NEW_OFFER', initNewOfferSaga),
    takeEvery('INIT_STATE_WITH_OFFER', initStateWithOfferSaga),
  ]);
}
